/**
 * Shim for `react-native-modal-datetime-picker`. Presents a small dialog with a
 * native `<input type="datetime-local|date|time">` and Confirm/Cancel actions.
 */
import React, { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import moment from 'moment';

export interface DateTimePickerModalProps {
  isVisible: boolean;
  mode?: 'datetime' | 'date' | 'time';
  date?: Date;
  onConfirm: (date: Date) => void;
  onCancel?: () => void;
  minimumDate?: Date;
  maximumDate?: Date;
  [key: string]: any;
}

function toInputValue(date: Date, mode: string): string {
  const m = moment(date);
  if (mode === 'date') return m.format('YYYY-MM-DD');
  if (mode === 'time') return m.format('HH:mm');
  return m.format('YYYY-MM-DDTHH:mm');
}

const DateTimePickerModal = function DateTimePickerModal({
  isVisible,
  mode = 'datetime',
  date,
  onConfirm,
  onCancel,
}: DateTimePickerModalProps) {
  const [value, setValue] = useState(() => toInputValue(date ?? new Date(), mode));

  useEffect(() => {
    if (isVisible) setValue(toInputValue(date ?? new Date(), mode));
  }, [isVisible, date, mode]);

  if (!isVisible) return null;

  const inputType = mode === 'date' ? 'date' : mode === 'time' ? 'time' : 'datetime-local';

  return createPortal(
    <div
      onClick={(e) => e.target === e.currentTarget && onCancel?.()}
      style={{
        position: 'fixed',
        inset: 0,
        background: 'rgba(0,0,0,0.5)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 1100,
      }}
    >
      <div style={{ background: '#fff', borderRadius: 12, padding: 20, minWidth: 260, boxShadow: '0 6px 24px rgba(0,0,0,0.3)' }}>
        <input
          type={inputType}
          value={value}
          onChange={(e) => setValue(e.target.value)}
          style={{ width: '100%', fontSize: 16, padding: 8, boxSizing: 'border-box' }}
        />
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 16, marginTop: 16 }}>
          <button onClick={onCancel} style={{ border: 'none', background: 'transparent', color: '#888', cursor: 'pointer', fontSize: 15 }}>
            Cancel
          </button>
          <button
            onClick={() => onConfirm(moment(value, ['YYYY-MM-DDTHH:mm', 'YYYY-MM-DD', 'HH:mm']).toDate())}
            style={{ border: 'none', background: 'transparent', color: '#00A3E0', cursor: 'pointer', fontWeight: 600, fontSize: 15 }}
          >
            Confirm
          </button>
        </div>
      </div>
    </div>,
    document.body,
  );
};

export default DateTimePickerModal;
